import { FLY_SPEED_REFERENCE_SPAN } from "./fly-speed"

/** At span=30, radius ≈ 1.65; scales linearly with scene span. */
export const LIGHT_MARKER_RADIUS_AT_REFERENCE = 0.22 * 5 * 1.5 * (FLY_SPEED_REFERENCE_SPAN / 30)

/** Center height floor: 2.5 sphere diameters above the ground plane. */
export const LIGHT_MARKER_MIN_Y_DIAMETERS = 2.5

export function lightMarkerRadius(sceneSpanMeters: number): number {
  const span = Number.isFinite(sceneSpanMeters) && sceneSpanMeters > 0 ? sceneSpanMeters : FLY_SPEED_REFERENCE_SPAN
  return LIGHT_MARKER_RADIUS_AT_REFERENCE * (span / FLY_SPEED_REFERENCE_SPAN)
}

/** Lowest allowed light-marker center Y (2.5 diameters above the floor). */
export function lightMarkerMinY(sceneSpanMeters: number): number {
  return lightMarkerRadius(sceneSpanMeters) * 2 * LIGHT_MARKER_MIN_Y_DIAMETERS
}

export type PointLightParams = {
  intensity: number
  distance: number
  decay: number
}

/**
 * Lamp reach/brightness so edge presets still light the platform center. Intensity is boosted
 * (x2 over the original tuning) specifically so the lamp has a visible local falloff instead of
 * being washed out by the always-on studio rig — see ambientRigFactorForLampIntensity below.
 */
export function pointLightParams(sceneSpanMeters: number): PointLightParams {
  const span = Number.isFinite(sceneSpanMeters) && sceneSpanMeters > 0 ? sceneSpanMeters : FLY_SPEED_REFERENCE_SPAN
  const distance = Math.max(span * 2.5, span)
  return {
    intensity: 12 * (distance / 20),
    distance,
    decay: 1,
  }
}

/** Shadow near plane as a fraction of lamp distance. Default 0.5 clips a centimeter stage. */
const POINT_SHADOW_NEAR_FRACTION = 0.01

export function pointShadowNearForSpan(sceneSpanMeters: number): number {
  return pointLightParams(sceneSpanMeters).distance * POINT_SHADOW_NEAR_FRACTION
}

/** Point-light shadow bias is a fraction of perspective depth, not meters. Near/far already follow span. */
export const POINT_SHADOW_BIAS = -0.0001

/** Absolute intensity slider bounds relative to span base. */
export function pointLightIntensityRange(sceneSpanMeters: number): { base: number; min: number; max: number } {
  const base = pointLightParams(sceneSpanMeters).intensity
  return { base, min: base * 0.2, max: base * 5 }
}

/** New-scene lamp level: one third of the slider maximum for this span. */
export function defaultPointLightIntensity(sceneSpanMeters: number): number {
  return pointLightIntensityRange(sceneSpanMeters).max / 3
}

/**
 * Studio ambient (hemi/key/fill) brightness factor driven by the lamp-intensity slider, so
 * dragging the lamp to its minimum genuinely reads as dark instead of the fixed rig staying lit.
 * Plain linear across the whole slider (no kink at `base`, which sits near the low end and made
 * the old piecewise curve jump to "fully lit" after the first sixth of the travel). Capped at 1 —
 * the rig never exceeds the palette's normal brightness, so brightening further up the slider
 * reads as the lamp itself getting stronger, not the ambient flattening it out again.
 */
export function ambientRigFactorForLampIntensity(
  intensity: number,
  range: { base: number; min: number; max: number },
): number {
  const t = Math.max(0, Math.min(1, (intensity - range.min) / Math.max(range.max - range.min, 1e-6)))
  return 0.08 + 0.92 * t
}

/**
 * Sky brightness factor for the same slider — deliberately gentler and spread across the whole
 * travel (not anchored to `base`, which sits near the low end and made the rig's curve above
 * look "maxed out" after the first sixth of the slider, and bottom out too dark at the minimum).
 */
export function skyAmbientFactorForLampIntensity(
  intensity: number,
  range: { base: number; min: number; max: number },
): number {
  const t = Math.max(0, Math.min(1, (intensity - range.min) / Math.max(range.max - range.min, 1e-6)))
  return 0.55 + 0.5 * t
}
